import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { WorkflowVersion } from '@models/Workflow'
import { firstValueFrom } from 'rxjs'
import { environment } from 'src/environments/environment'
import { DbService } from '../db.service'
import { FileShareService } from './file-share.service'

/** Result of the pure, offline key-remapping pass. */
export interface WorkflowCopyResult {
  /** Remapped draft, version 1. Still carries the SOURCE asset urls. */
  draft: WorkflowVersion
  /** True when at least one nested-workflow step was dropped. */
  skippedNestedWorkflow: boolean
}

/** Result of the asset (file) copy pass. */
export interface AssetCopyResult {
  /** Number of references that stayed on the source url because the copy failed. */
  failedCount: number
}

/** One asset reference to copy: read the url, write the new url back. */
interface AssetRef {
  /** Human readable location, used for error logs only. */
  path: string
  /** Step id the asset belongs to, used to build the storage path. */
  stepId: string
  get(): string
  set(url: string): void
}

const TRANSLATION_ID_PATTERN = /^\$~#.+\$~#$/
const MAX_PARALLEL_TRANSFERS = 4
/** Condition types whose `value` holds an option key rather than raw user input. */
const OPTION_VALUE_TYPES = ['radio', 'multicheckbox']

@Injectable({
  providedIn: 'root'
})
export class WorkflowCopyService {

  constructor(
    private dbService: DbService,
    private fileShareService: FileShareService,
    private http: HttpClient
  ) {}

  /**
   * Builds a fully independent copy of a workflow version.
   *
   * Pure and synchronous: no network access, and the source object is not mutated.
   * Every step id, widget key, option value and translation id is regenerated, and
   * every reference between them is remapped consistently.
   *
   * Asset urls are left pointing at the source. Call `copyAssets` afterwards, once
   * the target workflow id exists.
   */
  buildCopy(sourceVersion: WorkflowVersion, copyNameSuffix: string): WorkflowCopyResult {
    const draft: WorkflowVersion = JSON.parse(JSON.stringify(sourceVersion))

    const stepIdMap = new Map<string, string>()
    const itemKeyMap = new Map<string, string>()
    const optionValueMap = new Map<string, string>()
    const translationIdMap = new Map<string, string>()
    /** old widget key -> widget type, so conditions know how to read `value`. */
    const itemTypeMap = new Map<string, string>()

    // ---- Pass 1: generate every new id and fill the maps -------------------
    // Done before any reference is rewritten, because forward references
    // (connections.next, parentId) point at steps we have not visited yet.

    const newTranslationId = (oldId: string): string => {
      if (!this.isTranslationRef(oldId)) { return oldId }
      let mapped = translationIdMap.get(oldId)
      if (!mapped) {
        mapped = `$~#${this.dbService.createPushId()}$~#`
        translationIdMap.set(oldId, mapped)
      }
      return mapped
    }

    draft.name = newTranslationId(draft.name)
    draft.description = newTranslationId(draft.description)

    const sourceSteps = draft.steps ? draft.steps : {}
    const sourceStepIds = Object.keys(sourceSteps)

    // Nested (sub) workflow steps are out of scope: their content lives in
    // another workflow, so a consistent copy is a separate problem.
    const nestedStepIds = new Set<string>(
      sourceStepIds.filter(stepId => this.isNestedWorkflowStep(sourceSteps[stepId]))
    )
    const keptStepIds = sourceStepIds.filter(stepId => !nestedStepIds.has(stepId))

    keptStepIds.forEach(stepId => stepIdMap.set(stepId, this.dbService.createPushId()))

    keptStepIds.forEach(stepId => {
      const step: any = sourceSteps[stepId]
      const data = step?.data
      if (!data) { return }

      data.name = newTranslationId(data.name)

      const layouts = Array.isArray(data.layouts) ? data.layouts : []
      layouts.forEach((layout: any) => {
        const item = layout?.items?.[0]
        if (!item) { return }
        this.remapWidgetKeys(item, itemKeyMap, itemTypeMap, optionValueMap, translationIdMap, newTranslationId)
      })
    })

    // ---- Pass 2: rewrite references using the maps -------------------------

    const remappedSteps: Record<string, any> = {}
    keptStepIds.forEach(stepId => {
      const step: any = sourceSteps[stepId]
      const data = step?.data
      if (data) {
        data.next = this.mapStepRef(data.next, stepIdMap)
        data.parentId = this.mapStepRef(data.parentId, stepIdMap)

        if (Array.isArray(data.connections)) {
          data.connections = data.connections
            .map((connection: any) => this.remapConnection(connection, stepIdMap, itemKeyMap, itemTypeMap, optionValueMap))
            // Drop connections whose target was a nested-workflow step; keeping
            // them would leave dangling ids in the graph.
            .filter((connection: any) => connection !== null)
        }
      }
      remappedSteps[stepIdMap.get(stepId)] = step
    })
    draft.steps = remappedSteps

    draft.initialStep = this.mapStepRef(draft.initialStep, stepIdMap)
    if (!draft.initialStep) {
      // The old entry point was a dropped step; fall back to the lowest-order kept step.
      draft.initialStep = this.firstStepIdByOrder(remappedSteps)
    }

    draft.translations = this.remapTranslations(draft.translations, translationIdMap)

    // Nested workflow content is not carried over.
    delete (draft as any).subWorkflows

    this.appendCopySuffix(draft, copyNameSuffix)

    draft.version = 1
    ;(draft as any).dataVersion = environment.dataVersion

    return { draft, skippedNestedWorkflow: nestedStepIds.size > 0 }
  }

  /**
   * Copies every media asset of `draft` into the target workflow's own storage
   * path and rewrites the references in place, so the copy no longer depends on
   * the source workflow.
   *
   * The target account is resolved inside `uploadWorfklowFile` from the current
   * user, so copying into another account only needs that single call to take a
   * target account - this flow does not change.
   *
   * Partial failures are tolerated: a reference that cannot be copied stays on
   * the source url and is counted, rather than failing the whole duplicate.
   */
  async copyAssets(draft: WorkflowVersion, targetWorkflowId: string): Promise<AssetCopyResult> {
    const refs = this.collectAssetRefs(draft)
    if (refs.length === 0) { return { failedCount: 0 } }

    // The same url can appear in several places (e.g. one image reused in two
    // steps). Transfer it once and share the result.
    const transfers = new Map<string, Promise<string>>()
    let failedCount = 0
    let active: Promise<void>[] = []

    const runRef = async (ref: AssetRef): Promise<void> => {
      const sourceUrl = ref.get()
      let transfer = transfers.get(sourceUrl)
      if (!transfer) {
        transfer = this.transferAsset(sourceUrl, targetWorkflowId, ref.stepId)
        transfers.set(sourceUrl, transfer)
      }
      try {
        ref.set(await transfer)
      } catch (error) {
        // Leave the reference on the source url so the copy stays usable.
        failedCount++
        console.error(
          `[workflow-copy] asset copy failed, keeping source url. field=${ref.path} url=${sourceUrl}`,
          error
        )
      }
    }

    for (const ref of refs) {
      active.push(runRef(ref))
      if (active.length >= MAX_PARALLEL_TRANSFERS) {
        await Promise.all(active)
        active = []
      }
    }
    await Promise.all(active)

    return { failedCount }
  }

  // ---------------------------------------------------------------------------
  // Key remapping helpers
  // ---------------------------------------------------------------------------

  private isTranslationRef(value: any): boolean {
    return typeof value === 'string' && TRANSLATION_ID_PATTERN.test(value)
  }

  private isNestedWorkflowStep(step: any): boolean {
    return step?.data?.layouts?.[0]?.items?.[0]?.type === 'workflow'
  }

  private mapStepRef(stepId: any, stepIdMap: Map<string, string>): any {
    if (!stepId) { return stepId === '' ? '' : null }
    return stepIdMap.has(stepId) ? stepIdMap.get(stepId) : null
  }

  private firstStepIdByOrder(steps: Record<string, any>): string {
    const ids = Object.keys(steps)
    if (ids.length === 0) { return '' }
    return ids.sort((a, b) => (steps[a]?.data?.order ?? 0) - (steps[b]?.data?.order ?? 0))[0]
  }

  /** Regenerates the keys owned by one widget (and everything nested in it). */
  private remapWidgetKeys(
    item: any,
    itemKeyMap: Map<string, string>,
    itemTypeMap: Map<string, string>,
    optionValueMap: Map<string, string>,
    translationIdMap: Map<string, string>,
    newTranslationId: (id: string) => string
  ): void {
    if (item.key) {
      const newKey = this.dbService.createPushId()
      itemKeyMap.set(item.key, newKey)
      itemTypeMap.set(item.key, item.type)
      item.key = newKey
    }

    const templateOptions = item.templateOptions
    if (!templateOptions) { return }

    templateOptions.label = newTranslationId(templateOptions.label)
    templateOptions.placeholder = newTranslationId(templateOptions.placeholder)
    templateOptions.html = newTranslationId(templateOptions.html)

    // Video widgets built by `workflow-from-video` carry their transcript per
    // chapter as translation ids (`steps[].text`). Without remapping,
    // `remapTranslations` would drop those dictionary entries and the copy
    // would lose every transcript line.
    if (Array.isArray(templateOptions.steps)) {
      templateOptions.steps.forEach((chapterStep: any) => {
        chapterStep.text = newTranslationId(chapterStep.text)
      })
    }

    this.remapOptions(templateOptions.options, optionValueMap, translationIdMap, newTranslationId)

    if (templateOptions.tableData) {
      this.remapTableCells(templateOptions.tableData.columns, itemKeyMap, itemTypeMap, optionValueMap, translationIdMap, newTranslationId)
      this.remapTableCells(templateOptions.tableData.rows, itemKeyMap, itemTypeMap, optionValueMap, translationIdMap, newTranslationId)
    }

    if (Array.isArray(templateOptions.formData)) {
      templateOptions.formData.forEach((formItem: any) => {
        formItem.label = newTranslationId(formItem.label)
        if (Array.isArray(formItem.fields)) {
          formItem.fields.forEach((field: any) => {
            if (field.key) {
              const newFieldKey = this.dbService.createPushId()
              itemKeyMap.set(field.key, newFieldKey)
              itemTypeMap.set(field.key, field.type)
              field.key = newFieldKey
            }
            if (field.templateOptions) {
              field.templateOptions.label = newTranslationId(field.templateOptions.label)
              field.templateOptions.placeholder = newTranslationId(field.templateOptions.placeholder)
              this.remapOptions(field.templateOptions.options, optionValueMap, translationIdMap, newTranslationId)
            }
          })
        }
      })
    }
  }

  /**
   * Regenerates radio/multicheckbox options.
   *
   * `value` and `label` must keep sharing one push id, because
   * `widget-properties` builds them as `value = key` and `label = $~#key$~#`.
   * Breaking that link makes the widget properties screen unable to edit options.
   */
  private remapOptions(
    options: any,
    optionValueMap: Map<string, string>,
    translationIdMap: Map<string, string>,
    newTranslationId: (id: string) => string
  ): void {
    if (!Array.isArray(options)) { return }
    options.forEach((option: any) => {
      const oldValue = option.value
      const oldLabel = option.label
      const derivedFromValue = oldValue && oldLabel === `$~#${oldValue}$~#`
      const newValue = this.dbService.createPushId()
      if (oldValue) { optionValueMap.set(oldValue, newValue) }

      if (derivedFromValue) {
        option.value = newValue
        option.label = `$~#${newValue}$~#`
        // Map the OLD label to this exact new label, so the translations
        // dictionary is rebuilt under it. Generating a separate id here would
        // silently drop the option text.
        translationIdMap.set(oldLabel, option.label)
      } else {
        if (oldValue) { option.value = newValue }
        option.label = newTranslationId(oldLabel)
      }
    })
  }

  private remapTableCells(
    rows: any,
    itemKeyMap: Map<string, string>,
    itemTypeMap: Map<string, string>,
    optionValueMap: Map<string, string>,
    translationIdMap: Map<string, string>,
    newTranslationId: (id: string) => string
  ): void {
    if (!Array.isArray(rows)) { return }
    rows.forEach((row: any) => {
      if (!Array.isArray(row?.cells)) { return }
      row.cells.forEach((cell: any) => {
        if (cell.key) {
          const newCellKey = this.dbService.createPushId()
          itemKeyMap.set(cell.key, newCellKey)
          itemTypeMap.set(cell.key, cell.type)
          cell.key = newCellKey
        }
        if (cell.templateOptions) {
          cell.templateOptions.label = newTranslationId(cell.templateOptions.label)
          cell.templateOptions.placeholder = newTranslationId(cell.templateOptions.placeholder)
          this.remapOptions(cell.templateOptions.options, optionValueMap, translationIdMap, newTranslationId)
        }
      })
    })
  }

  /**
   * Rewrites one connection. Returns null when the connection pointed at a
   * dropped step and therefore has to go.
   */
  private remapConnection(
    connection: any,
    stepIdMap: Map<string, string>,
    itemKeyMap: Map<string, string>,
    itemTypeMap: Map<string, string>,
    optionValueMap: Map<string, string>
  ): any {
    const hadNext = !!connection.next
    connection.next = this.mapStepRef(connection.next, stepIdMap)
    if (hadNext && !connection.next) { return null }

    if (Array.isArray(connection.conditions)) {
      connection.conditions.forEach((condition: any) => {
        const sourceItemKey = condition.type
        // For radio/multicheckbox the value is an option key. For input, number
        // and datepicker it is raw user input and must stay untouched, so the
        // widget type decides - not the shape of the value.
        const widgetType = itemTypeMap.get(sourceItemKey)
        if (OPTION_VALUE_TYPES.includes(widgetType) && optionValueMap.has(condition.value)) {
          condition.value = optionValueMap.get(condition.value)
        }
        // `type` holds the widget key the condition is checked against.
        if (sourceItemKey && itemKeyMap.has(sourceItemKey)) {
          condition.type = itemKeyMap.get(sourceItemKey)
        }
      })
    }
    return connection
  }

  /** Rebuilds every language dictionary under the new translation ids. */
  private remapTranslations(translations: any, translationIdMap: Map<string, string>): any {
    if (!translations) { return translations }
    const remapped: Record<string, any> = {}
    Object.keys(translations).forEach(lang => {
      const source = translations[lang]
      if (!source) {
        remapped[lang] = source
        return
      }
      const target: Record<string, any> = {}
      Object.keys(source).forEach(oldId => {
        const newId = translationIdMap.get(oldId)
        // Ids with no mapping belonged to dropped steps; leave them behind.
        if (newId) { target[newId] = source[oldId] }
      })
      remapped[lang] = target
    })
    return remapped
  }

  /** Appends " (Copy)" to the name in the default language only. */
  private appendCopySuffix(draft: WorkflowVersion, copyNameSuffix: string): void {
    const lang = draft.defaultLanguage
    const dictionary = draft.translations?.[lang]
    if (!dictionary || !draft.name) { return }
    const currentName = dictionary[draft.name]
    dictionary[draft.name] = `${currentName ? currentName : ''}${copyNameSuffix}`
  }

  // ---------------------------------------------------------------------------
  // Asset copy helpers
  // ---------------------------------------------------------------------------

  /** Collects every asset reference in the draft as a get/set pair. */
  private collectAssetRefs(draft: WorkflowVersion): AssetRef[] {
    const refs: AssetRef[] = []

    const push = (path: string, stepId: string, get: () => string, set: (url: string) => void): void => {
      if (this.isCopyableUrl(get())) { refs.push({ path, stepId, get, set }) }
    }

    const steps: any = draft.steps ? draft.steps : {}
    Object.keys(steps).forEach(stepId => {
      const data = steps[stepId]?.data
      if (!data) { return }

      const layouts = Array.isArray(data.layouts) ? data.layouts : []
      layouts.forEach((layout: any, layoutIndex: number) => {
        const item = layout?.items?.[0]
        const options = item?.templateOptions
        if (!options) { return }
        const at = `steps.${stepId}.layouts[${layoutIndex}]`

        push(`${at}.imgUrl`, stepId, () => options.imgUrl, url => options.imgUrl = url)
        push(`${at}.videoUrl`, stepId, () => options.videoUrl, url => options.videoUrl = url)
        push(`${at}.pdfUrl`, stepId, () => options.pdfUrl, url => options.pdfUrl = url)

        if (Array.isArray(options.images)) {
          options.images.forEach((image: any, imageIndex: number) => {
            push(`${at}.images[${imageIndex}].imgUrl`, stepId, () => image.imgUrl, url => image.imgUrl = url)
            push(
              `${at}.images[${imageIndex}].imgThumbnailUrl`,
              stepId,
              () => image.imgThumbnailUrl,
              url => image.imgThumbnailUrl = url
            )
          })
        }
      })

      if (Array.isArray(data.files)) {
        data.files.forEach((file: any, fileIndex: number) => {
          // The file id identifies the copy, not the original.
          file.id = this.dbService.createPushId()
          push(`steps.${stepId}.files[${fileIndex}].url`, stepId, () => file.url, url => file.url = url)
        })
      }
    })

    const firstStepId = Object.keys(steps)[0] ? Object.keys(steps)[0] : 'workflow'

    if (Array.isArray(draft.files)) {
      draft.files.forEach((file: any, fileIndex: number) => {
        if (file.id) { file.id = this.dbService.createPushId() }
        push(`files[${fileIndex}].url`, firstStepId, () => file.url, url => file.url = url)
      })
    }

    const videoFlowData: any = (draft as any).videoFlowData
    if (videoFlowData) {
      push('videoFlowData.url', firstStepId, () => videoFlowData.url, url => videoFlowData.url = url)
    }

    // Rich-text widget content is stored per language as an uploaded html file.
    // A raw html string is left alone: the first save uploads it under the new
    // workflow anyway.
    const translations: any = draft.translations
    if (translations) {
      const htmlIds = this.collectHtmlTranslationIds(steps)
      Object.keys(translations).forEach(lang => {
        const dictionary = translations[lang]
        if (!dictionary) { return }
        htmlIds.forEach(({ htmlId, stepId }) => {
          push(
            `translations.${lang}.${htmlId}`,
            stepId,
            () => dictionary[htmlId],
            url => dictionary[htmlId] = url
          )
        })
      })
    }

    return refs
  }

  private collectHtmlTranslationIds(steps: any): { htmlId: string, stepId: string }[] {
    const found: { htmlId: string, stepId: string }[] = []
    Object.keys(steps).forEach(stepId => {
      const layouts = steps[stepId]?.data?.layouts
      if (!Array.isArray(layouts)) { return }
      layouts.forEach((layout: any) => {
        const item = layout?.items?.[0]
        if (item?.type === 'html' && item.templateOptions?.html) {
          found.push({ htmlId: item.templateOptions.html, stepId })
        }
      })
    })
    return found
  }

  /** True for values that are an http(s) url we can download and re-upload. */
  private isCopyableUrl(value: any): boolean {
    return typeof value === 'string' && /^https?:\/\//i.test(value.trim())
  }

  /** Downloads one asset and re-uploads it under the target workflow. */
  private async transferAsset(sourceUrl: string, targetWorkflowId: string, stepId: string): Promise<string> {
    const blob = await firstValueFrom(this.http.get(sourceUrl, { responseType: 'blob' }))
    const file = new File([blob], this.fileNameFromUrl(sourceUrl), { type: blob.type })
    return this.fileShareService.uploadWorfklowFile(
      file,
      targetWorkflowId,
      stepId,
      this.dbService.createPushId()
    )
  }

  /** Derives a file name (with extension) from a storage url. */
  private fileNameFromUrl(url: string): string {
    try {
      const withoutQuery = url.split('?')[0]
      const decoded = decodeURIComponent(withoutQuery)
      const last = decoded.split('/').pop()
      if (last && last.includes('.')) { return last }
      return last ? last : 'file'
    } catch {
      return 'file'
    }
  }
}
